import * as React from 'react';

import { debounce } from 'lodash';
import { IStyleConfig } from '@cian/utils';

import { PikLink } from '../pik_link/pik_link';
import { Scroller } from './scroller';
import { connect } from 'react-redux';
import { IApplicationState } from '../../types/redux';
import { ITabsState } from '../../types/tabs';
import { changeHash, changeTabs } from '../../actions/location';

export type FragmentId = 'catalog' | 'mortgage' | 'online-purchase' | 'pikstandart' | 'offices' | 'about';

export interface ILinkOwnProps {
  to: FragmentId;
  className?: string;
  activeClass?: string;
  style?: React.CSSProperties;
  tabs?: Partial<ITabsState>;
}

interface IProps {
  changeHash(newHash: string): void;
  changeTabs(newTabs: Partial<ITabsState>): void;
}

interface ILinkStoreProps {
  hash: string;
  stateTabs: ITabsState;
}

export type ILinkProps = ILinkOwnProps & IProps & ILinkStoreProps;

const scroller = new Scroller<FragmentId>({ disableNativeScrollRestoration: true });

export class LinkComponent extends React.PureComponent<React.PropsWithChildren<ILinkProps>, object> {
  public componentDidMount() {
    window.addEventListener('scroll', this.updateHashDebounce);
  }

  public componentWillUnmount() {
    window.removeEventListener('scroll', this.updateHashDebounce);
  }

  public render() {
    const { className, style, activeClass, children, hash, to, tabs, stateTabs } = this.props;
    let isLinkActive = hash === to;

    if (tabs && isLinkActive) {
      isLinkActive = Object.keys(tabs).every((tabKey: keyof ITabsState) => tabs[tabKey] === stateTabs[tabKey]);
    }

    const styles = [className, isLinkActive && activeClass, style].filter(Boolean);

    return (
      <PikLink to={{ hash: this.props.to }} pikStyles={styles as IStyleConfig} onClick={this.onClick} mergeQuery>
        {children}
      </PikLink>
    );
  }

  private onClick = () => {
    const { to, tabs } = this.props;

    if (tabs) {
      this.props.changeTabs(tabs);
    }

    scroller.scrollTo(to).catch(err => console.error(err));
  };

  private updateHash = () => {
    const { hash, to } = this.props;
    if (hash !== to && scroller.fragmentInViewport(this.props.to)) {
      this.props.changeHash(this.props.to);
    }
  };

  private updateHashDebounce = debounce(this.updateHash, 100);
}

export const Link = connect(
  (state: IApplicationState) => ({
    hash: state.location.hash,
    stateTabs: state.tabs,
  }),
  {
    changeHash,
    changeTabs,
  },
)(LinkComponent);

interface IFragmentOwnProps {
  name: FragmentId;
  className?: string;
  style?: React.CSSProperties;
  offset?: number;
}

interface IFragmentStoreProps {
  hash: string;
}

export type IFragmentProps = IFragmentOwnProps & IFragmentStoreProps;

class FragmentComponent extends React.PureComponent<React.PropsWithChildren<IFragmentProps>, object> {
  private node: HTMLElement | null;

  public componentDidMount() {
    if (!this.node) {
      throw new Error('node has no linked');
    }

    scroller.registerFragment({
      name: this.props.name,
      node: this.node,
      offset: this.props.offset || 0,
    });

    const { hash, name } = this.props;

    if (hash === name) {
      scroller.scrollTo(name, 0).catch(err => console.error(err));
    }
  }

  public UNSAFE_componentWillReceiveProps(newProps: IFragmentProps) {
    const hashChanged = newProps.hash !== this.props.hash;
    const isThisFragmentsHash = this.props.name === newProps.hash;

    if (hashChanged && isThisFragmentsHash && !scroller.fragmentInViewport(this.props.name)) {
      scroller.scrollTo(this.props.name).catch(err => console.error(err));
    }
  }

  public render() {
    const { className, style, children } = this.props;

    return (
      <div className={className} style={style} ref={node => (this.node = node)}>
        {children}
      </div>
    );
  }
}

export const Fragment = connect((state: IApplicationState) => ({
  hash: state.location.hash,
}))(FragmentComponent);
